/**
 * Profile-aware paste sanitizer used by iframe bridge runtime and tests.
 * This module intentionally keeps one implementation source of truth via a
 * factory function and exposes it both as normal JS exports and inline script.
 */

import { escapeHTML as sharedEscapeHTML } from '../shared/html-escape.js';
import { escapeForInlineScript } from '../shared/section-js-facade.js';

function createPasteSanitizerRuntime(env, helpers = {}) {
  const htmlEscape = helpers?.escapeHTML;
  if (typeof htmlEscape !== 'function') {
    throw new Error('[paste-sanitizer] escapeHTML helper is required');
  }

  const PROFILE_KEEP_FORMATTING = 'keep-formatting';
  const PROFILE_MATCH_SITE_STYLE = 'match-site-style';
  const PROFILE_PLAIN_TEXT = 'plain-text';
  const DEFAULT_PROFILE = PROFILE_MATCH_SITE_STYLE;
  const MAX_SOURCE_SIZE_BYTES = 1024 * 1024;
  const MAX_NODE_COUNT = 5000;

  const INLINE_COLOR_CLASS_BY_TOKEN = Object.freeze({
    accent: 'pb-inline-color-accent',
    heading: 'pb-inline-color-heading',
    body: 'pb-inline-color-body'
  });
  const INLINE_COLOR_CLASSES = Object.values(INLINE_COLOR_CLASS_BY_TOKEN);

  const DEFAULT_TOKEN_COLORS = Object.freeze({
    accent: '#ff6a3e',
    heading: '#1a1a1a',
    body: '#4e4b66'
  });

  const FORBIDDEN_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'IFRAME',
    'OBJECT',
    'EMBED',
    'FORM',
    'INPUT',
    'TEXTAREA',
    'SELECT',
    'OPTION',
    'BUTTON',
    'META',
    'LINK',
    'BASE',
    'FRAME',
    'FRAMESET',
    'APPLET',
    'AUDIO',
    'VIDEO',
    'SOURCE',
    'TRACK'
  ]);

  const INLINE_ALLOWED_TAGS = new Set(['STRONG', 'B', 'EM', 'I', 'A', 'BR', 'SPAN']);
  const PROSE_ALLOWED_TAGS = new Set([
    'STRONG',
    'B',
    'EM',
    'I',
    'A',
    'BR',
    'SPAN',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'UL',
    'OL',
    'LI',
    'BLOCKQUOTE',
    'CODE',
    'PRE',
    'P',
    'HR'
  ]);
  const HEADING_EDITABLE_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BUTTON']);
  const BLOCK_BREAK_TAGS = new Set([
    'P',
    'DIV',
    'LI',
    'UL',
    'OL',
    'BLOCKQUOTE',
    'PRE',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6'
  ]);
  const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
  const SAFE_IMAGE_DATA_MIME = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

  const NAMED_COLOR_RGB = Object.freeze({
    black: [0, 0, 0],
    white: [255, 255, 255],
    red: [255, 0, 0],
    green: [0, 128, 0],
    blue: [0, 0, 255],
    gray: [128, 128, 128],
    grey: [128, 128, 128],
    yellow: [255, 255, 0],
    orange: [255, 165, 0],
    purple: [128, 0, 128]
  });

  function normalizeProfile(value) {
    if (value === PROFILE_KEEP_FORMATTING) return PROFILE_KEEP_FORMATTING;
    if (value === PROFILE_PLAIN_TEXT) return PROFILE_PLAIN_TEXT;
    return PROFILE_MATCH_SITE_STYLE;
  }

  function byteLengthUtf8(value) {
    const text = String(value || '');
    if (typeof TextEncoder === 'function') {
      return new TextEncoder().encode(text).length;
    }
    return unescape(encodeURIComponent(text)).length;
  }

  function ensureDOMParser() {
    if (!env || typeof env.DOMParser !== 'function') {
      throw new Error('[paste-sanitizer] DOMParser is required');
    }
    return env.DOMParser;
  }

  function parseHtmlToRoot(html) {
    const DOMParserCtor = ensureDOMParser();
    const doc = new DOMParserCtor().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild || doc.body;
    return { doc, root };
  }

  function plainTextToHtml(text) {
    const normalized = String(text || '').replace(/\r\n?/g, '\n');
    return htmlEscape(normalized).replace(/\n/g, '<br>');
  }

  function extractPlainText(root) {
    const chunks = [];
    const walk = (node) => {
      const children = Array.from(node.childNodes || []);
      for (const child of children) {
        if (child.nodeType === 3) {
          chunks.push(child.nodeValue || '');
          continue;
        }
        if (child.nodeType !== 1) continue;
        const tag = child.tagName;
        if (tag === 'BR') {
          chunks.push('\n');
          continue;
        }
        walk(child);
        if (BLOCK_BREAK_TAGS.has(tag)) chunks.push('\n');
      }
    };
    walk(root);
    return chunks
      .join('')
      .replace(/\r\n?/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function normalizePlainTextHtmlFromSource(sourceHtml) {
    const { root } = parseHtmlToRoot(sourceHtml);
    const plain = extractPlainText(root);
    return htmlEscape(plain).replace(/\n/g, '<br>');
  }

  function hasUnsafeDataImage(href) {
    const raw = String(href || '')
      .trim()
      .toLowerCase();
    if (!raw.startsWith('data:')) return false;
    const commaIndex = raw.indexOf(',');
    if (commaIndex <= 5) return true;
    const mime = raw.slice(5, commaIndex).split(';')[0].trim();
    return !SAFE_IMAGE_DATA_MIME.has(mime);
  }

  function isSafeHref(href) {
    const value = String(href || '').trim();
    if (!value) return false;
    const lower = value.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('vbscript:')) return false;
    if (lower.startsWith('data:')) return false;
    if (value.startsWith('#')) return true;
    if (value.startsWith('/')) return true;
    if (value.startsWith('./') || value.startsWith('../')) return true;
    if (value.startsWith('//')) return true;
    try {
      const parsed = new URL(value, 'https://example.invalid');
      return SAFE_LINK_PROTOCOLS.has(parsed.protocol);
    } catch (err) {
      return false;
    }
  }

  function removeNodePreserveChildren(node) {
    if (!node || !node.parentNode) return;
    const parent = node.parentNode;
    while (node.firstChild) {
      parent.insertBefore(node.firstChild, node);
    }
    parent.removeChild(node);
  }

  function unwrapHeadingBlockWithSeparator(node, doc) {
    if (!node || !node.parentNode) return;
    const parent = node.parentNode;
    let previous = node.previousSibling;
    while (previous && previous.nodeType === 3 && !String(previous.nodeValue || '').trim()) {
      previous = previous.previousSibling;
    }
    if (previous && !(previous.nodeType === 1 && previous.tagName === 'BR')) {
      parent.insertBefore(doc.createElement('br'), node);
    }
    removeNodePreserveChildren(node);
  }

  function hasNormalFontWeight(el) {
    if (!el?.style) return false;
    const weight = String(el.style.fontWeight || '')
      .trim()
      .toLowerCase();
    return weight === 'normal' || weight === '400';
  }

  function replaceElementTag(el, nextTag, doc) {
    if (!el || !doc || !nextTag || el.tagName === nextTag) return el;
    const replacement = doc.createElement(nextTag);
    const attrs = Array.from(el.attributes || []);
    for (const attr of attrs) {
      replacement.setAttribute(attr.name, attr.value);
    }
    while (el.firstChild) {
      replacement.appendChild(el.firstChild);
    }
    if (el.parentNode) {
      el.parentNode.replaceChild(replacement, el);
    }
    return replacement;
  }

  function normalizeTagName(tagName) {
    const t = String(tagName || '').toUpperCase();
    return t || 'P';
  }

  function isHeadingTag(tagName) {
    return /^H[1-6]$/.test(String(tagName || '').toUpperCase());
  }

  function resolveAllowedTags({ profile, targetIsHeading }) {
    if (profile === PROFILE_PLAIN_TEXT) return new Set(['BR']);
    if (targetIsHeading) return INLINE_ALLOWED_TAGS;
    return PROSE_ALLOWED_TAGS;
  }

  function parseHexColor(color) {
    const hex = String(color || '')
      .trim()
      .toLowerCase();
    if (!hex.startsWith('#')) return null;
    if (hex.length === 4) {
      const r = parseInt(hex[1] + hex[1], 16);
      const g = parseInt(hex[2] + hex[2], 16);
      const b = parseInt(hex[3] + hex[3], 16);
      return Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) ? [r, g, b] : null;
    }
    if (hex.length === 7) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) ? [r, g, b] : null;
    }
    return null;
  }

  function parseRgbColor(color) {
    const raw = String(color || '')
      .trim()
      .toLowerCase();
    const match = raw.match(/^rgba?\(([^)]+)\)$/);
    if (!match) return null;
    const parts = match[1].split(',').map((part) => part.trim());
    if (parts.length < 3) return null;
    const rgb = [];
    for (let i = 0; i < 3; i += 1) {
      const val = Number.parseFloat(parts[i]);
      if (!Number.isFinite(val)) return null;
      rgb.push(Math.max(0, Math.min(255, Math.round(val))));
    }
    return rgb;
  }

  function parseColorToRgb(color) {
    const raw = String(color || '').trim();
    if (!raw) return null;
    const named = NAMED_COLOR_RGB[raw.toLowerCase()];
    if (named) return named.slice();
    return parseHexColor(raw) || parseRgbColor(raw);
  }

  function srgbToLinear(value) {
    const v = value / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }

  function rgbToLab(rgb) {
    if (!Array.isArray(rgb) || rgb.length < 3) return null;
    const r = srgbToLinear(rgb[0]);
    const g = srgbToLinear(rgb[1]);
    const b = srgbToLinear(rgb[2]);

    const x = (r * 0.4124564 + g * 0.3575761 + b * 0.1804375) / 0.95047;
    const y = (r * 0.2126729 + g * 0.7151522 + b * 0.072175) / 1.0;
    const z = (r * 0.0193339 + g * 0.119192 + b * 0.9503041) / 1.08883;

    const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
    const fx = f(x);
    const fy = f(y);
    const fz = f(z);

    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  function labDistance(a, b) {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const dL = a[0] - b[0];
    const dA = a[1] - b[1];
    const dB = a[2] - b[2];
    return Math.sqrt(dL * dL + dA * dA + dB * dB);
  }

  function parseInlineColorFromStyle(styleValue) {
    const style = String(styleValue || '');
    if (!style) return null;
    const colorDecl = style
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => /^color\s*:/i.test(entry));
    if (!colorDecl) return null;
    const colorValue = colorDecl.split(':').slice(1).join(':').trim();
    return parseColorToRgb(colorValue);
  }

  function normalizeTokenColors(tokenColors) {
    const source =
      tokenColors && typeof tokenColors === 'object' ? tokenColors : DEFAULT_TOKEN_COLORS;
    const result = {};
    for (const token of ['accent', 'heading', 'body']) {
      result[token] =
        parseColorToRgb(source[token]) || parseColorToRgb(DEFAULT_TOKEN_COLORS[token]);
    }
    return result;
  }

  function resolveNearestColorToken(styleRgb, tokenColors) {
    if (!styleRgb) return null;
    const styleLab = rgbToLab(styleRgb);
    if (!styleLab) return null;
    let bestToken = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const token of ['accent', 'heading', 'body']) {
      const tokenRgb = tokenColors[token];
      if (!tokenRgb) continue;
      const tokenLab = rgbToLab(tokenRgb);
      const dist = labDistance(styleLab, tokenLab);
      if (dist < bestDistance) {
        bestDistance = dist;
        bestToken = token;
      }
    }
    return bestToken;
  }

  function removeUnsafeAttributes(el) {
    const attrs = Array.from(el.attributes || []);
    for (const attr of attrs) {
      const name = attr.name;
      const lower = name.toLowerCase();
      if (lower === 'style') {
        el.removeAttribute(name);
        continue;
      }
      if (lower.startsWith('on')) {
        el.removeAttribute(name);
        continue;
      }
      if (lower === 'srcdoc' || lower === 'formaction') {
        el.removeAttribute(name);
        continue;
      }
      if (lower === 'xmlns:xlink') {
        el.removeAttribute(name);
        continue;
      }
      if (lower === 'xlink:href') {
        const value = String(attr.value || '').trim();
        if (!value.startsWith('#')) {
          el.removeAttribute(name);
        }
      }
    }
  }

  function normalizeSpanClasses(el) {
    if (el.tagName !== 'SPAN') {
      el.removeAttribute('class');
      return;
    }
    const classes = String(el.getAttribute('class') || '')
      .split(/\s+/)
      .map((value) => value.trim())
      .filter(Boolean);
    const safe = classes.filter((cls) => INLINE_COLOR_CLASSES.includes(cls));
    if (safe.length) {
      el.setAttribute('class', safe.join(' '));
    } else {
      el.removeAttribute('class');
    }
  }

  function applyTokenClassToElement(el, token, doc) {
    const className = INLINE_COLOR_CLASS_BY_TOKEN[token];
    if (!className || !el) return;
    if (el.tagName === 'SPAN') {
      const existing = String(el.getAttribute('class') || '')
        .split(/\s+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .filter((cls) => !INLINE_COLOR_CLASSES.includes(cls));
      existing.push(className);
      el.setAttribute('class', existing.join(' '));
      return;
    }

    const wrapper = doc.createElement('span');
    wrapper.setAttribute('class', className);
    while (el.firstChild) {
      wrapper.appendChild(el.firstChild);
    }
    el.appendChild(wrapper);
  }

  function sanitizeLinkAttrs(el) {
    const href = el.getAttribute('href');
    if (!href || !isSafeHref(href) || hasUnsafeDataImage(href)) {
      el.removeAttribute('href');
    }
    const target = String(el.getAttribute('target') || '').trim();
    if (target === '_blank') {
      el.setAttribute('rel', 'noopener noreferrer');
    } else {
      el.removeAttribute('target');
      el.removeAttribute('rel');
    }
  }

  function enforceAllowedAttributes(el) {
    if (el.tagName === 'A') {
      const allowed = new Set(['href', 'target', 'rel']);
      const attrs = Array.from(el.attributes || []);
      for (const attr of attrs) {
        if (!allowed.has(attr.name.toLowerCase())) {
          el.removeAttribute(attr.name);
        }
      }
      sanitizeLinkAttrs(el);
      return;
    }
    if (el.tagName === 'SPAN') {
      const attrs = Array.from(el.attributes || []);
      for (const attr of attrs) {
        if (attr.name.toLowerCase() !== 'class') {
          el.removeAttribute(attr.name);
        }
      }
      normalizeSpanClasses(el);
      return;
    }
    const attrs = Array.from(el.attributes || []);
    for (const attr of attrs) {
      el.removeAttribute(attr.name);
    }
  }

  function sanitizeElementTree(root, options) {
    const profile = normalizeProfile(options.profile);
    const targetTagName = normalizeTagName(options.targetTagName);
    const targetIsHeading = !!options.targetIsHeading;
    const allowedTags = resolveAllowedTags({ profile, targetIsHeading });
    const tokenColors = normalizeTokenColors(options.tokenColors);
    const doc = root.ownerDocument;

    const walk = (node) => {
      const children = Array.from(node.childNodes || []);
      for (const child of children) {
        if (child.nodeType !== 1) continue;
        let el = child;
        const tag = el.tagName;

        if (FORBIDDEN_TAGS.has(tag)) {
          el.remove();
          continue;
        }

        // Google Docs wraps the entire clipboard fragment in a visual-reset
        // <b style="font-weight:normal">. Preserve its children, not the bold.
        if ((tag === 'B' || tag === 'STRONG') && hasNormalFontWeight(el)) {
          walk(el);
          removeNodePreserveChildren(el);
          continue;
        }

        if (profile === PROFILE_MATCH_SITE_STYLE && !targetIsHeading && isHeadingTag(tag)) {
          const nextHeading = isHeadingTag(targetTagName) ? targetTagName : 'P';
          el = replaceElementTag(el, nextHeading, doc);
        }

        if (!allowedTags.has(el.tagName)) {
          // Sanitize descendants before lifting them into the allowed tree.
          walk(el);
          if (targetIsHeading && BLOCK_BREAK_TAGS.has(el.tagName)) {
            unwrapHeadingBlockWithSeparator(el, doc);
          } else {
            removeNodePreserveChildren(el);
          }
          continue;
        }

        const styleColor =
          profile === PROFILE_MATCH_SITE_STYLE
            ? parseInlineColorFromStyle(el.getAttribute('style') || '')
            : null;

        removeUnsafeAttributes(el);
        enforceAllowedAttributes(el);

        walk(el);

        if (profile === PROFILE_MATCH_SITE_STYLE && styleColor) {
          const token = resolveNearestColorToken(styleColor, tokenColors);
          if (token) {
            applyTokenClassToElement(el, token, doc);
          }
        }
      }
    };

    walk(root);
  }

  function truncateToNodeLimit(root, limit) {
    if (!root || !Number.isFinite(limit) || limit <= 0) return false;
    const nodes = Array.from(root.querySelectorAll('*'));
    if (nodes.length <= limit) return false;
    for (let i = limit; i < nodes.length; i += 1) {
      const node = nodes[i];
      if (!node || !node.parentNode) continue;
      removeNodePreserveChildren(node);
    }
    return true;
  }

  function sanitizeClipboardPayload(input = {}) {
    const requestedProfile = normalizeProfile(input.profile);
    const sourceHtml = String(input.html || '').trim();
    const sourceText = String(input.text || '');
    const targetTagName = normalizeTagName(input.targetTagName);
    const targetIsHeading =
      input.targetIsHeading === true || HEADING_EDITABLE_TAGS.has(targetTagName);
    const maxSourceBytes = Number.isFinite(input.maxSourceBytes)
      ? input.maxSourceBytes
      : MAX_SOURCE_SIZE_BYTES;
    const maxNodeCount = Number.isFinite(input.maxNodeCount) ? input.maxNodeCount : MAX_NODE_COUNT;

    const sourceType = sourceHtml ? 'html' : 'text';
    const sourceForSanitization = sourceHtml || plainTextToHtml(sourceText);
    const bytes = byteLengthUtf8(sourceForSanitization);
    if (bytes > maxSourceBytes) {
      return {
        ok: false,
        rejected: true,
        reason: 'source-too-large',
        profile: requestedProfile,
        sourceType,
        html: '',
        truncated: false
      };
    }

    if (requestedProfile === PROFILE_PLAIN_TEXT) {
      return {
        ok: true,
        rejected: false,
        reason: '',
        profile: PROFILE_PLAIN_TEXT,
        sourceType,
        html: normalizePlainTextHtmlFromSource(sourceForSanitization),
        truncated: false
      };
    }

    const { root } = parseHtmlToRoot(sourceForSanitization);
    sanitizeElementTree(root, {
      profile: requestedProfile,
      targetTagName,
      targetIsHeading,
      tokenColors: input.tokenColors
    });
    const truncated = truncateToNodeLimit(root, maxNodeCount);

    return {
      ok: true,
      rejected: false,
      reason: truncated ? 'node-limit-truncated' : '',
      profile: requestedProfile,
      sourceType,
      html: root.innerHTML,
      truncated
    };
  }

  return {
    PROFILES: Object.freeze({
      KEEP_FORMATTING: PROFILE_KEEP_FORMATTING,
      MATCH_SITE_STYLE: PROFILE_MATCH_SITE_STYLE,
      PLAIN_TEXT: PROFILE_PLAIN_TEXT
    }),
    DEFAULT_PROFILE,
    MAX_SOURCE_SIZE_BYTES,
    MAX_NODE_COUNT,
    sanitizeClipboardPayload
  };
}

const runtime = createPasteSanitizerRuntime(globalThis, { escapeHTML: sharedEscapeHTML });

export const PASTE_PROFILES = runtime.PROFILES;
export const DEFAULT_PASTE_PROFILE = runtime.DEFAULT_PROFILE;
export const PASTE_MAX_SOURCE_SIZE_BYTES = runtime.MAX_SOURCE_SIZE_BYTES;
export const PASTE_MAX_NODE_COUNT = runtime.MAX_NODE_COUNT;

export function sanitizeClipboardPayload(input) {
  return runtime.sanitizeClipboardPayload(input);
}

export function generatePasteSanitizerScript({ guestFrame = false } = {}) {
  const factoryExpr = `(${createPasteSanitizerRuntime.toString()})`;
  const escapeExpr = `(${sharedEscapeHTML.toString()})`;
  if (guestFrame) {
    // Native function expressions preserve the same sanitizer in a frame whose
    // policy permits inline scripts but intentionally disallows eval.
    return `
(function() {
  'use strict';
  var __pbCreatePasteSanitizerRuntime = ${escapeForInlineScript(factoryExpr)};
  var __pbEscapeHTML = ${escapeForInlineScript(escapeExpr)};
  window.__pbPasteSanitizer = __pbCreatePasteSanitizerRuntime(window, { escapeHTML: __pbEscapeHTML });
})();
`;
  }
  return `
(function() {
  'use strict';
  var __pbCreatePasteSanitizerRuntime = (0, eval)(${JSON.stringify(factoryExpr)});
  var __pbEscapeHTML = (0, eval)(${JSON.stringify(escapeExpr)});
  window.__pbPasteSanitizer = __pbCreatePasteSanitizerRuntime(window, { escapeHTML: __pbEscapeHTML });
})();
`;
}
